import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Heart, User, LayoutDashboard, Users, BookOpen, 
  Calendar, Shield, GraduationCap, Clock, Target, X 
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useProfile } from "@/hooks/useProfile";

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  highlightSelector?: string;
}

// Core tour steps shown to ALL users
const CORE_TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to EAB Connect",
    description: "EAB Connect is a relationship-centered platform designed to help entrepreneurs learn, connect, and grow through intentional relationships.",
    icon: Heart,
  },
  {
    id: "profile",
    title: "Your Profile",
    description: "Your profile is how others get to know you. Keep it up to date so connections, mentors, and collaborators understand your background and goals.",
    icon: User,
    highlightSelector: "[data-tour='profile']",
  },
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Your dashboard is your home base. It shows your progress, upcoming activity, and what's next for you on the platform.",
    icon: LayoutDashboard,
    highlightSelector: "[data-tour='dashboard']",
  },
  {
    id: "connections",
    title: "Connections",
    description: "Connections are ongoing professional relationships—just like LinkedIn. Connecting helps you build your network, but it never creates an obligation.",
    icon: Users,
    highlightSelector: "[data-tour='connections']",
  },
  {
    id: "learning",
    title: "Learning",
    description: "Learning is how you build readiness. Courses are designed to prepare you with the fundamentals needed to get the most out of mentorship and community support.",
    icon: BookOpen,
    highlightSelector: "[data-tour='learning']",
  },
  {
    id: "events",
    title: "Events",
    description: "Events bring the community together. Join workshops, discussions, and live sessions to learn and connect in real time.",
    icon: Calendar,
    highlightSelector: "[data-tour='events']",
  },
];

// Mentor-specific branch steps
const MENTOR_BRANCH_STEPS: TourStep[] = [
  {
    id: "mentor-terms",
    title: "Mentorship, Your Way",
    description: "You decide if, when, and how you mentor. Availability and topics are always under your control.",
    icon: Shield,
  },
  {
    id: "mentor-prepared",
    title: "Prepared Entrepreneurs",
    description: "Entrepreneurs complete learning courses before requesting mentorship, so conversations are focused and productive.",
    icon: GraduationCap,
  },
  {
    id: "mentor-no-spam",
    title: "No Spam, No Obligation",
    description: "Mentorship requests are opt-in. You can accept, decline, or ignore requests at any time.",
    icon: Shield,
  },
];

// Entrepreneur-specific branch steps
const ENTREPRENEUR_BRANCH_STEPS: TourStep[] = [
  {
    id: "entrepreneur-preparation",
    title: "Prepare Before Mentorship",
    description: "Mentorship on EAB Connect is intentional. Before requesting mentorship, you'll complete learning courses designed to prepare you for productive, focused sessions.",
    icon: GraduationCap,
  },
  {
    id: "entrepreneur-learning-first",
    title: "Why Learning Comes First",
    description: "Completing courses helps you clarify your goals, understand key concepts, and respect mentors' time. Prepared entrepreneurs get better mentorship.",
    icon: BookOpen,
  },
  {
    id: "entrepreneur-requests",
    title: "Mentorship Requests",
    description: "Once you've completed the required learning, you'll be able to submit a mentorship request. Mentors review requests in their own time and choose whether to engage.",
    icon: Target,
  },
  {
    id: "entrepreneur-respect",
    title: "Respect the Process",
    description: "Mentorship is not on-demand. It's a thoughtful, short-term exchange built on readiness, respect, and clear expectations.",
    icon: Clock,
  },
];

interface OnboardingTourProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
  onCoreComplete?: () => void;
  onRoleComplete?: () => void;
}

export function OnboardingTour({ 
  isOpen, 
  onComplete, 
  onSkip,
  onCoreComplete,
  onRoleComplete,
}: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [phase, setPhase] = useState<"core" | "role">("core");
  const [highlightPosition, setHighlightPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const navigate = useNavigate();
  const { data: profile } = useProfile();

  // Determine which role branch to show
  const userRole = profile?.role;
  const roleBranchSteps = userRole === "mentor" ? MENTOR_BRANCH_STEPS : ENTREPRENEUR_BRANCH_STEPS;
  
  // Current step list based on phase
  const currentSteps = phase === "core" ? CORE_TOUR_STEPS : roleBranchSteps;
  const step = currentSteps[currentStep];
  const isLastStepOfPhase = currentStep === currentSteps.length - 1;
  const isLastStepOverall = phase === "role" && isLastStepOfPhase;
  const Icon = step?.icon || Heart;

  // Calculate total steps for progress display
  const totalCoreSteps = CORE_TOUR_STEPS.length;
  const totalRoleSteps = roleBranchSteps.length;
  const totalSteps = totalCoreSteps + totalRoleSteps;
  const currentOverallStep = phase === "core" ? currentStep : totalCoreSteps + currentStep;

  useEffect(() => {
    if (!isOpen || !step?.highlightSelector) {
      setHighlightPosition(null);
      return;
    }

    const updateHighlight = () => {
      const element = document.querySelector(step.highlightSelector!);
      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setHighlightPosition(null);
      }
    };

    const timer = setTimeout(updateHighlight, 100);
    window.addEventListener("resize", updateHighlight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateHighlight);
    };
  }, [isOpen, currentStep, phase, step?.highlightSelector]);

  const handleNext = () => {
    if (isLastStepOfPhase) {
      if (phase === "core") {
        // Finished core, move to role branch
        onCoreComplete?.();
        setPhase("role");
        setCurrentStep(0);
      } else {
        // Finished role branch, complete the tour
        onRoleComplete?.();
        onComplete();
        navigate("/profile");
      }
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else if (phase === "role") {
      // Go back to the last core step
      setPhase("core");
      setCurrentStep(CORE_TOUR_STEPS.length - 1);
    }
  };

  if (!isOpen || !step) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[100] bg-foreground/60 backdrop-blur-sm" />

      {/* Highlight cutout */}
      {highlightPosition && (
        <div
          className="fixed z-[101] ring-4 ring-primary ring-offset-2 ring-offset-transparent rounded-lg pointer-events-none transition-all duration-300"
          style={{
            top: highlightPosition.top - 4,
            left: highlightPosition.left - 4,
            width: highlightPosition.width + 8,
            height: highlightPosition.height + 8,
          }}
        />
      )}

      {/* Tour Card */}
      <div className="fixed inset-0 z-[102] flex items-center justify-center p-4 pointer-events-none">
        <Card className="max-w-lg w-full shadow-2xl pointer-events-auto animate-fade-in">
          <CardHeader className="relative pb-2">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={onSkip}
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">{step.title}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Step {currentOverallStep + 1} of {totalSteps}
                  {phase === "role" && (
                    <span className="ml-2 text-xs text-primary">
                      ({userRole === "mentor" ? "For Mentors" : "For Entrepreneurs"})
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{step.description}</p>
          </CardContent>
          <CardFooter className="flex items-center justify-between pt-2">
            {/* Progress dots */}
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-2 w-2 rounded-full transition-colors",
                    index === currentOverallStep ? "bg-primary" : 
                    index < currentOverallStep ? "bg-primary/50" : "bg-muted"
                  )}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {(currentStep > 0 || phase === "role") && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              <Button onClick={handleNext}>
                {isLastStepOverall ? "Complete & Edit Profile" : "Next"}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </>
  );
}
